import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

// GDPR / PECR compliant cookie banner.
// - Strictly necessary cookies always on (required for site function)
// - Analytics / ads cookies load only after explicit accept
// - Decline preserves user choice; Manage allows granular toggle
// Consent stored in localStorage as JSON: { necessary: true, analytics: bool, ads: bool, ts: ISO }

const CONSENT_KEY = 'homego-cookie-consent';

const CookieConsent = () => {
  const [show, setShow] = useState(false);
  const [manage, setManage] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, ads: true });
  const { pathname } = useLocation();
  const hiddenRoute = pathname.startsWith('/crm') || pathname.startsWith('/admin') ||
    pathname.startsWith('/quote/') || pathname.startsWith('/site-visit/') || pathname.startsWith('/upload-media/') || pathname.startsWith('/track/');

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null');
      if (!stored) setShow(true);
      else applyConsent(stored);
    } catch {
      setShow(true);
    }
  }, []);

  const applyConsent = (consent) => {
    // Expose consent to dataLayer so GTM/GA4 can gate tags
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cookie_consent_updated',
      analytics_consent: !!consent.analytics,
      ad_consent: !!consent.ads
    });
    // Google Consent Mode v2
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': consent.ads ? 'granted' : 'denied',
        'analytics_storage': consent.analytics ? 'granted' : 'denied',
        'ad_user_data': consent.ads ? 'granted' : 'denied',
        'ad_personalization': consent.ads ? 'granted' : 'denied'
      });
    }
    if (typeof window.fbq === 'function') window.fbq('consent', consent.ads ? 'grant' : 'revoke');
  };

  const save = (consent) => {
    const payload = { necessary: true, ...consent, ts: new Date().toISOString() };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
    applyConsent(payload);
    setShow(false);
  };

  const acceptAll = () => save({ analytics: true, ads: true });
  const declineAll = () => save({ analytics: false, ads: false });
  const savePrefs = () => save(prefs);

  if (!show || hiddenRoute) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] bg-gray-950 text-white border-t-2 border-gold-500 shadow-2xl md:bottom-16 md:left-4 md:right-auto md:max-w-md md:rounded-lg md:border-2"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
    >
      <div className="p-4 md:p-5">
        <div className="flex items-start gap-3">
          <Cookie className="w-6 h-6 text-gold-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-white font-bold text-base md:text-lg mb-1.5">We value your privacy</h3>
            <p className="text-gray-200 text-xs md:text-sm leading-relaxed mb-3">
              This website uses cookies to make the site work, understand how visitors use it, and measure which ads bring enquiries. Click "Accept all" to agree, "Decline" to use only strictly-necessary cookies, or "Manage" for granular control.{' '}
              <a href="/cookies-policy" className="text-gold-400 underline font-semibold" data-testid="cookie-policy-link">
                Read our Cookies Policy
              </a>
            </p>

            {manage && (
              <div className="mb-3 space-y-2 bg-black rounded p-3 border border-gold-500/20" data-testid="cookie-manage-panel">
                <label className="flex items-center justify-between text-xs text-gray-300 cursor-not-allowed">
                  <span><strong className="text-white">Strictly necessary</strong> — required for site to work</span>
                  <input type="checkbox" checked disabled className="accent-gold-500" data-testid="cookie-toggle-necessary" readOnly />
                </label>
                <label className="flex items-center justify-between text-xs text-gray-300 cursor-pointer" data-testid="cookie-toggle-analytics">
                  <span><strong className="text-white">Analytics</strong> — GA4 (how visitors use the site)</span>
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                    className="accent-gold-500"
                  />
                </label>
                <label className="flex items-center justify-between text-xs text-gray-300 cursor-pointer" data-testid="cookie-toggle-ads">
                  <span><strong className="text-white">Advertising</strong> — Google Ads conversion tracking</span>
                  <input
                    type="checkbox"
                    checked={prefs.ads}
                    onChange={(e) => setPrefs({ ...prefs, ads: e.target.checked })}
                    className="accent-gold-500"
                  />
                </label>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              {!manage && (
                <button
                  onClick={() => setManage(true)}
                  className="px-3 py-2 border border-gold-500/40 text-gold-400 hover:bg-gold-500/10 rounded text-xs font-semibold"
                  data-testid="cookie-manage-btn"
                >
                  Manage
                </button>
              )}
              <button
                onClick={declineAll}
                className="px-3 py-2 border border-white/40 bg-gray-800 text-white hover:bg-gray-700 rounded text-xs font-semibold"
                data-testid="cookie-decline-btn"
              >
                Decline
              </button>
              {manage ? (
                <button
                  onClick={savePrefs}
                  className="px-4 py-2 bg-gradient-to-r from-gold-500 to-gold-400 text-black font-bold rounded text-xs"
                  data-testid="cookie-save-prefs-btn"
                >
                  Save preferences
                </button>
              ) : (
                <button
                  onClick={acceptAll}
                  className="px-4 py-2 bg-gradient-to-r from-gold-500 to-gold-400 text-black font-bold rounded text-xs"
                  data-testid="cookie-accept-btn"
                >
                  Accept all
                </button>
              )}
            </div>
          </div>
          <button
            onClick={declineAll}
            aria-label="Close cookie banner and decline"
            className="text-gray-300 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
